/**
 * Canvas Editor — simplified overlay-control modal for funnel creatives.
 *
 * Keeps the Gemini-rendered vizuál intact and lets the admin tweak the
 * Pillow text overlay (1-sec re-render) without re-paying an image-gen call.
 * Controls: headline position · accent color · font size · corner badge.
 */
import { useState } from "react";
import axios from "axios";
import { X, Check, Loader2, Palette } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  { key: "top",    label: "Hore" },
  { key: "middle", label: "Stred" },
  { key: "bottom", label: "Dole" },
];

const SWATCHES = ["#D4AF37", "#F59E0B", "#FF3B30", "#C084FC", "#60A5FA", "#10B981", "#FFFFFF"];

const BADGE_PRESETS = ["", "FREE", "NEW", "-50%", "-70%", "HOT", "TOP"];

export default function CanvasEditorModal({ creative, onClose, onSaved }) {
  const cfg = creative.overlay_config || {};
  const [form, setForm] = useState({
    headline: creative.headline || "",
    cta: creative.cta || "",
    headline_position: cfg.headline_position || "bottom",
    accent_color: cfg.accent_color || "",
    font_size: cfg.font_size || 0,
    badge_text: cfg.badge_text || "",
    badge_color: cfg.badge_color || "#ef4444",
  });
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(creative.media_url);

  function resolveUrl(u) {
    if (!u) return "";
    return u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  }

  async function apply() {
    setBusy(true);
    try {
      const payload = {
        headline: form.headline,
        cta: form.cta,
        headline_position: form.headline_position,
        accent_color: form.accent_color || null,
        font_size: form.font_size > 0 ? form.font_size : null,
        badge_text: form.badge_text || null,
        badge_color: form.badge_color,
      };
      const { data } = await axios.post(
        `${API}/funnel/admin/creatives/${creative.id}/reapply-overlay`,
        payload,
        { headers: { "X-User-Email": JSON.parse(localStorage.getItem("user") || "{}").email || "admin@toprankpulse.com" } },
      );
      // Cache-bust the preview so admin sees the new render instantly.
      setPreviewUrl(`${data.url}?t=${Date.now()}`);
    } catch (e) {
      window.alert(`Overlay zlyhalo: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function saveAndClose() {
    await apply();
    if (onSaved) await onSaved();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      data-testid="canvas-editor-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col lg:flex-row shadow-2xl">
        {/* Preview pane */}
        <div className="lg:w-1/2 bg-gray-900 p-6 flex items-center justify-center relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black text-white rounded-full"
            data-testid="canvas-editor-close"
          >
            <X size={18} />
          </button>
          {previewUrl ? (
            <img
              src={resolveUrl(previewUrl)}
              alt="preview"
              className="max-w-full max-h-[70vh] rounded-lg shadow-lg"
              data-testid="canvas-editor-preview"
            />
          ) : (
            <p className="text-gray-400 text-sm">Žiadny obrázok — najprv klikni GEN</p>
          )}
        </div>

        {/* Controls pane */}
        <div className="lg:w-1/2 p-6 overflow-y-auto text-gray-800">
          <h2 className="text-lg font-black mb-1 flex items-center gap-2">
            <Palette size={18} /> Canvas Editor
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            Zmeny sa aplikujú cez Pillow overlay — ~1 sekunda namiesto 25s Gemini re-gen.
          </p>

          {/* Headline */}
          <Field label="Headline">
            <textarea
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              rows={2}
              data-testid="canvas-headline"
              className="w-full text-sm bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-amber-400"
            />
          </Field>

          {/* CTA */}
          <Field label="CTA text">
            <input
              value={form.cta}
              onChange={(e) => setForm({ ...form, cta: e.target.value })}
              data-testid="canvas-cta"
              className="w-full text-sm bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-amber-400"
            />
          </Field>

          {/* Position */}
          <Field label="Pozícia headline">
            <div className="flex gap-1" data-testid="canvas-position">
              {POSITIONS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setForm({ ...form, headline_position: p.key })}
                  data-testid={`canvas-position-${p.key}`}
                  className={`flex-1 py-1.5 text-xs font-bold rounded ${form.headline_position === p.key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </Field>

          {/* Accent color */}
          <Field label="Accent farba">
            <div className="flex items-center gap-1.5 flex-wrap">
              {SWATCHES.map((s) => (
                <button
                  key={s}
                  onClick={() => setForm({ ...form, accent_color: s })}
                  data-testid={`canvas-accent-${s.replace("#","")}`}
                  style={{ background: s }}
                  className={`w-7 h-7 rounded-full border-2 transition ${form.accent_color === s ? "border-gray-900 scale-110" : "border-gray-200 hover:border-gray-400"}`}
                />
              ))}
              <input
                type="text"
                value={form.accent_color}
                onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                placeholder="#hex"
                className="w-20 text-xs font-mono bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5"
              />
            </div>
          </Field>

          {/* Font size */}
          <Field label={`Font size: ${form.font_size > 0 ? `${form.font_size}px` : "AUTO"}`}>
            <input
              type="range" min="0" max="140" step="2"
              value={form.font_size}
              onChange={(e) => setForm({ ...form, font_size: parseInt(e.target.value, 10) })}
              data-testid="canvas-font-size"
              className="w-full"
            />
          </Field>

          {/* Badge */}
          <Field label="Badge (pravý horný roh)">
            <div className="flex flex-col gap-2">
              <div className="flex gap-1 flex-wrap" data-testid="canvas-badges">
                {BADGE_PRESETS.map((b) => (
                  <button
                    key={b || "none"}
                    onClick={() => setForm({ ...form, badge_text: b })}
                    className={`px-2 py-1 text-xs font-bold rounded ${form.badge_text === b ? "bg-red-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                  >
                    {b || "— žiadny"}
                  </button>
                ))}
              </div>
              {form.badge_text && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Farba:</span>
                  <input
                    type="color"
                    value={form.badge_color}
                    onChange={(e) => setForm({ ...form, badge_color: e.target.value })}
                    className="h-7 w-10 cursor-pointer border border-gray-200 rounded"
                  />
                </div>
              )}
            </div>
          </Field>

          {/* Actions */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={apply}
              disabled={busy}
              data-testid="canvas-apply-btn"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 font-black text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:opacity-90 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Palette className="w-4 h-4" />}
              Aplikovať
            </button>
            <button
              onClick={saveAndClose}
              disabled={busy}
              data-testid="canvas-save-btn"
              className="flex items-center gap-2 px-4 font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-60"
            >
              <Check size={16} /> Uložiť
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-[11px] font-black text-gray-600 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );
}
